import { Big } from "big.js";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  coerceToSafeInt,
  CoerceToSafeIntStatus,
  SafeInt,
} from "@every.org/common/src/codecs/number";
import { Currency } from "@every.org/common/src/entity/types";
import {
  DECIMAL_OFFSET_FOR_CURRENCY,
  minimumDenominationAmountToCurrencyValue,
} from "@every.org/common/src/helpers/currency";

import { Icon, IconDisplay, IconSize } from "src/components/Icon";
import { Popover } from "src/components/Popover";
import { RadioInput } from "src/components/RadioInput";
import { TextInput, TextInputType } from "src/components/TextInput";
import { LabeledThermometer } from "src/components/Thermometer/LabeledThermometer";
import {
  Fieldset,
  RadioGroup,
} from "src/pages/Fundraiser/EditForm/FundraiserOwnerPicker/styles";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";

interface GoalInputProps {
  goal: SafeInt | undefined | null;
  currency: Currency;
  setGoal: (goal: SafeInt | undefined | null) => void;
}

enum GoalType {
  AUTO,
  MANUAL,
  NO_GOAL,
}

export function GoalInput({ goal, setGoal, currency }: GoalInputProps) {
  const [goalType, setGoalType] = useState(
    goal ? (goal === 0 ? GoalType.NO_GOAL : GoalType.MANUAL) : GoalType.AUTO
  );

  const goalInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (goal === 0) {
      setGoalType(GoalType.NO_GOAL);
    }
  }, [goal, setGoalType]);

  useEffect(() => {
    if (goalType === GoalType.AUTO) {
      setGoal(null);
    } else if (goalType === GoalType.NO_GOAL) {
      setGoal(0 as SafeInt);
    }
  }, [goalType, setGoal]);

  const inputValue = goal
    ? minimumDenominationAmountToCurrencyValue({
        currency,
        amountInMinDenom: goal,
      }).amount.toString()
    : "";

  const onGoalChange = useCallback(
    (strValue: string) => {
      if (strValue === "") {
        setGoal(null);
        return;
      }
      const usedDecimal = strValue.includes(".");
      if (usedDecimal || !/^[0-9]+$/g.test(strValue)) {
        if (usedDecimal && goal && goalInputRef.current) {
          goalInputRef.current.blur();
        }
        return;
      }

      try {
        const newGoal = coerceToSafeInt({
          num:
            Number.parseInt(strValue) *
            Math.pow(10, DECIMAL_OFFSET_FOR_CURRENCY[currency]),
        });

        if (
          newGoal.status === CoerceToSafeIntStatus.IS_SAFE_INT &&
          newGoal.num >= 0
        ) {
          setGoal(newGoal.num);
          setGoalType(GoalType.MANUAL);
        }
      } catch (e) {
        return;
      }
    },
    [currency, goal, setGoal]
  );

  return (
    <Fieldset name="goal">
      <legend>Fundraiser goal</legend>
      <RadioGroup>
        <RadioInput
          data-tname="AutoGoal"
          id="AutoGoal"
          name="goal"
          value={GoalType.AUTO}
          checked={goalType === GoalType.AUTO}
          onChange={() => setGoalType(GoalType.AUTO)}
          label={
            <div css={[horizontalStackCss.xs, { alignItems: "center" }]}>
              <span>Use Every.org smart milestones</span>
              <Popover
                placement={"top-start"}
                content={
                  <div css={[verticalStackCss.m, { maxWidth: 300 }]}>
                    <LabeledThermometer
                      currency={currency}
                      goalAmount={new Big(200)}
                      currentAmount={new Big(125)}
                      monthly={null}
                      goalLabel="Next milestone"
                      progressLabel="Raised"
                    />
                    <p>
                      The first milestone is $100. When you pass that milestone,
                      the progress bar will update to the next milestone, using
                      goal psychology to optimize for something that feels both
                      motivating and achievable. Recommended if you don&apos;t
                      have a specific amount you need to raise.
                    </p>
                  </div>
                }
              >
                <Icon
                  iconImport={() => import("@components/Icon/icons/InfoIcon")}
                  display={IconDisplay.SECONDARY}
                  size={IconSize.X_SMALL}
                />
              </Popover>
            </div>
          }
        />
        <RadioInput
          data-tname="NoGoal"
          id="NoGoal"
          name="goal"
          value={GoalType.NO_GOAL}
          checked={goalType === GoalType.NO_GOAL}
          onChange={() => setGoalType(GoalType.NO_GOAL)}
          label={"No goal or progress bar"}
        />
        <RadioInput
          data-tname="ManualGoal"
          id="ManualGoal"
          name="goal"
          value={GoalType.MANUAL}
          checked={goalType === GoalType.MANUAL}
          onChange={() => setGoalType(GoalType.MANUAL)}
          label={"Set my own goal"}
        />
      </RadioGroup>
      <TextInput
        data-tname="GoalInput"
        type={TextInputType.TEXT}
        inputMode="numeric"
        ref={goalInputRef}
        value={inputValue || ""}
        onChange={(e) => onGoalChange(e.currentTarget.value)}
        placeholder="How much money do you want to raise?"
        collapseDescriptionSpace
        collapseLabelSpace
      />
    </Fieldset>
  );
}
